import React, { useState, useEffect, useCallback } from "react";
import { useSpring, animated } from "react-spring";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faFileContract,
  faBriefcase,
  faList,
  faCalculator,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "react-modal";
import { useTheme } from "ogcommon";
import JobListPage from "./JobTypeForm";
import ControlUsers from "./ControlUsers";
import MasterList from "./ItemMasterList";
import { Leases } from "ogcommon";
import { baseUrl } from "./config";

Modal.setAppElement("#root");

const Admin = () => {
  const { theme } = useTheme();
  const [activePanel, setActivePanel] = useState("jobs");
  const [activeSubPanel, setActiveSubPanel] = useState("jobList");
  const [dashBusy, setDashBusy] = useState(false);

  const panelAnimation = useSpring({
    to: { opacity: 1 },
    from: { opacity: 0 },
    config: { tension: 220, friction: 20 },
  });

  // Tailwind theme helpers
  const variant = (dark, light) => (theme === "dark" ? dark : light);
  const sidePanelClass = variant(
    "bg-gray-900 text-white",
    "bg-white text-gray-900"
  );
  const sidePanelHoverClass = variant("hover:bg-gray-700", "hover:bg-gray-100");
  const activePanelClass = variant(
    "bg-gray-700 text-white",
    "bg-gray-200 text-black"
  );

  const handleChangePanel = (panel, subPanel = "") => {
    setActivePanel(panel);
    setActiveSubPanel(subPanel);
  };

  useEffect(() => {
    setActivePanel("jobs");
    setActiveSubPanel("jobList");
  }, []);

  // --- New: Signed open of Company Home (Admin Dashboard) ---
  const openCompanyHome = useCallback(async () => {
    if (dashBusy) return;

    // Prefer route/prop in future; for now use hostname prefix
    const sub = window.location.hostname.split(".")[0];

    if (!sub) {
      alert("Missing subdomain / company");
      return;
    }

    try {
      setDashBusy(true);

      const r = await fetch(`${baseUrl}/api/make_token.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sub: String(sub),
          ttl: 3600,
          aud: "house.ogendeavors.com",
          scope: ["company:read", "tickets:read"],
        }),
      });

      const js = await r.json().catch(() => ({}));
      if (!r.ok || !js?.success || !js?.token) {
        throw new Error(js?.error || `Token mint failed (${r.status})`);
      }

      let target;
      try {
        target = js.redirect
          ? new URL(js.redirect, window.location.origin)
          : new URL(
              `https://house.ogendeavors.com/companyHome/${encodeURIComponent(
                sub
              )}`
            );
      } catch {
        target = new URL(
          `https://house.ogendeavors.com/companyHome/${encodeURIComponent(sub)}`
        );
      }

      target.searchParams.set("token", js.token);
      target.searchParams.set("src", "fieldticket");

      window.open(target.toString(), "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error(e);
      alert("Failed to open Admin Dashboard. See console for details.");
    } finally {
      setDashBusy(false);
    }
  }, [dashBusy]);

  return (
    <div
      className={`min-h-screen flex ${variant("bg-gray-900", "bg-gray-50")}`}
    >
      {/* Side Panel */}
      <div className={`w-64 ${sidePanelClass} fixed h-full shadow-2xl`}>
        <animated.div style={panelAnimation} className="p-6">
          <h1 className="text-2xl font-extrabold border-b-4 border-indigo-500 pb-3 mb-8">
            Control Panel
          </h1>

          <nav className="space-y-2">
            {/* ADMIN DASHBOARD (formerly "Company Home" button, now a menu item) */}
            <button
              type="button"
              onClick={openCompanyHome}
              disabled={dashBusy}
              className={`w-full px-6 py-4 rounded-lg flex items-center gap-3 transition-colors duration-200 ${sidePanelHoverClass} ${
                dashBusy ? "opacity-60 cursor-not-allowed" : ""
              }`}
              title="Open Admin Dashboard on ogSupport with a signed access token"
            >
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                className="text-xl shrink-0"
              />
              <span className="text-lg font-medium">
                {dashBusy ? "Opening…" : "Admin Dashboard"}
              </span>
            </button>

            {/* JOBS */}
            <button
              className={`w-full px-6 py-4 rounded-lg flex items-center gap-3 transition-colors duration-200 ${sidePanelHoverClass} ${
                activePanel === "jobs" ? activePanelClass : ""
              }`}
              onClick={() => handleChangePanel("jobs", "jobList")}
              type="button"
            >
              <FontAwesomeIcon
                icon={faBriefcase}
                className="text-xl shrink-0"
              />
              <span className="text-lg font-medium">Jobs</span>
            </button>

            {/* Job sub-nav */}
            {activePanel === "jobs" && (
              <div className="pl-8 space-y-2">
                <button
                  className={`w-full px-6 py-4 rounded-lg flex items-center gap-3 transition-colors duration-200 ${sidePanelHoverClass} ${
                    activeSubPanel === "jobList" ? activePanelClass : ""
                  }`}
                  onClick={() => handleChangePanel("jobs", "jobList")}
                  type="button"
                >
                  <FontAwesomeIcon icon={faList} className="text-lg shrink-0" />
                  <span className="text-md">Job List</span>
                </button>

                <button
                  className={`w-full px-6 py-4 rounded-lg flex items-center gap-3 transition-colors duration-200 ${sidePanelHoverClass} ${
                    activeSubPanel === "masterList" ? activePanelClass : ""
                  }`}
                  onClick={() => handleChangePanel("jobs", "masterList")}
                  type="button"
                >
                  <FontAwesomeIcon icon={faList} className="text-lg shrink-0" />
                  <span className="text-md">Master List</span>
                </button>
              </div>
            )}

            {/* LEASES (kept hidden in main nav as per original comments) */}
            {/* <button
              className={`w-full px-6 py-4 rounded-lg flex items-center gap-3 transition-colors duration-200 ${sidePanelHoverClass} ${
                activePanel === "leases" ? activePanelClass : ""
              }`}
              onClick={() => handleChangePanel("leases", "leaseList")}
              type="button"
            >
              <FontAwesomeIcon icon={faFileContract} className="text-xl shrink-0" />
              <span className="text-lg font-medium">Leases</span>
            </button> */}

            {activePanel === "leases" && (
              <div className="pl-8 space-y-2">
                <button
                  className={`w-full px-6 py-4 rounded-lg flex items-center gap-3 transition-colors duration-200 ${sidePanelHoverClass} ${
                    activeSubPanel === "leaseList" ? activePanelClass : ""
                  }`}
                  onClick={() => handleChangePanel("leases", "leaseList")}
                  type="button"
                >
                  <FontAwesomeIcon icon={faList} className="text-lg shrink-0" />
                  <span className="text-md">Lease List</span>
                </button>
              </div>
            )}

            {/* USERS (kept hidden in main nav as per original comments) */}
            {/* <button
              className={`w-full px-6 py-4 rounded-lg flex items-center gap-3 transition-colors duration-200 ${sidePanelHoverClass} ${
                activePanel === "users" ? activePanelClass : ""
              }`}
              onClick={() => handleChangePanel("users", "userList")}
              type="button"
            >
              <FontAwesomeIcon icon={faUsers} className="text-xl shrink-0" />
              <span className="text-lg font-medium">Users</span>
            </button> */}

            {activePanel === "users" && (
              <div className="pl-8 space-y-2">
                <button
                  className={`w-full px-6 py-4 rounded-lg flex items-center gap-3 transition-colors duration-200 ${sidePanelHoverClass} ${
                    activeSubPanel === "userList" ? activePanelClass : ""
                  }`}
                  onClick={() => handleChangePanel("users", "userList")}
                  type="button"
                >
                  <FontAwesomeIcon icon={faList} className="text-lg shrink-0" />
                  <span className="text-md">User List</span>
                </button>
              </div>
            )}
          </nav>
        </animated.div>
      </div>

      {/* Main Content */}
      <div className="flex-grow pl-72 p-10 overflow-y-auto">
        <animated.div style={panelAnimation}>
          {activePanel === "jobs" && activeSubPanel === "jobList" && (
            <JobListPage />
          )}
          {activePanel === "jobs" && activeSubPanel === "masterList" && (
            <MasterList />
          )}
          {activePanel === "leases" && activeSubPanel === "leaseList" && (
            <Leases baseUrl={baseUrl} />
          )}
          {activePanel === "users" && activeSubPanel === "userList" && (
            <ControlUsers />
          )}
        </animated.div>
      </div>
    </div>
  );
};

export default Admin;
